import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Frequently Bought Together (compact bundle widget)
 *
 * Rendered as a reorderable block inside the Main Product section. Row #1 is always the
 * current product (locked, never discounted). The remaining rows are suggested products
 * from the same collection, chosen at random from a hidden reserve pool that ships with
 * the page — so "Pick another" is an instant, no-reload swap and random selection differs
 * per refresh. Suggested variants stay in sync with the main product's option selection,
 * a flat bundle discount is shown live on the suggestions, and "Add all to cart" is AJAX.
 */

const SIZE_ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];
const SIZE_SYNONYMS = {
  'extra extra small': 'xxs',
  'extra small': 'xs',
  small: 's',
  medium: 'm',
  large: 'l',
  'extra large': 'xl',
  'x-large': 'xl',
  'xx-large': 'xxl',
  '2xl': 'xxl',
  '3xl': 'xxxl',
  '4xl': 'xxxxl',
};

/** @param {string | undefined | null} value @returns {number} rank in SIZE_ORDER, or -1 */
function sizeRank(value) {
  if (!value) return -1;
  let v = String(value).toLowerCase().trim();
  if (SIZE_SYNONYMS[v]) v = SIZE_SYNONYMS[v];
  return SIZE_ORDER.indexOf(v);
}

/** @returns {number | null} ordinal size distance, or null if either is not a known size */
function sizeDistance(a, b) {
  const ra = sizeRank(a);
  const rb = sizeRank(b);
  if (ra < 0 || rb < 0) return null;
  return Math.abs(ra - rb);
}

class FrequentlyBoughtTogether extends HTMLElement {
  /** @type {{name: string, value: string}[]} */
  #mainOptions = [];
  /** @type {Element | Document | null} */
  #section = null;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);

    this.#section = this.closest('.shopify-section') || document;
    this.#section.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#initPool();
    this.#mainOptions = this.#readMainOptions();
    for (const row of this.#activeRows()) {
      this.#syncRow(row);
      this.#applyDiscountDisplay(row);
    }
    this.#updateTotals();
    this.#updatePickAvailability();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  get #discountPct() {
    const pct = Number(this.dataset.bundleDiscount);
    return Number.isFinite(pct) && pct > 0 ? Math.min(100, pct) : 0;
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  #companions() {
    return this.querySelector('[data-fbt-list]');
  }

  /** Active = the locked current row plus the currently visible suggestions. */
  #activeRows() {
    return this.querySelectorAll('[data-fbt-row]:not([hidden])');
  }

  /* --------------------------------------------------------------------- */
  /* Random pool selection                                                 */
  /* --------------------------------------------------------------------- */

  #initPool() {
    const companions = this.#companions();
    if (!companions || this.dataset.randomize !== 'true') return;

    const rows = Array.from(companions.querySelectorAll('[data-fbt-row]'));
    const visibleCount = Math.max(1, Number(this.dataset.visibleCount) || rows.length);
    if (rows.length <= visibleCount) return;

    // Fisher–Yates shuffle, then reveal the first N and hide the rest.
    const shuffled = rows.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const visible = new Set(shuffled.slice(0, visibleCount));
    for (const row of rows) row.hidden = !visible.has(row);
  }

  /* --------------------------------------------------------------------- */
  /* Pick another (instant, no reload)                                     */
  /* --------------------------------------------------------------------- */

  /** @param {HTMLElement} button */
  #pickAnother(button) {
    const row = button.closest('[data-fbt-row]');
    const companions = this.#companions();
    if (!(row instanceof HTMLElement) || !companions) return;

    const reserve = Array.from(companions.querySelectorAll('[data-fbt-row][hidden]')).filter(
      (r) => r instanceof HTMLElement && r.dataset.productId !== row.dataset.productId
    );
    if (reserve.length === 0) return;

    const next = /** @type {HTMLElement} */ (reserve[Math.floor(Math.random() * reserve.length)]);

    // Fade the outgoing row out, then swap the incoming one into its slot and fade in.
    row.classList.add('fbt-row--swapping');
    window.setTimeout(() => {
      next.hidden = false;
      next.classList.add('fbt-row--swapping');
      row.insertAdjacentElement('afterend', next);
      row.hidden = true;
      row.classList.remove('fbt-row--swapping');

      this.#syncRow(next);
      this.#applyDiscountDisplay(next);
      this.#updateTotals();
      this.#updatePickAvailability();

      requestAnimationFrame(() => requestAnimationFrame(() => next.classList.remove('fbt-row--swapping')));
    }, 250);
  }

  /** Disable "Pick another" when the reserve pool is empty. */
  #updatePickAvailability() {
    const companions = this.#companions();
    const hasReserve = companions ? companions.querySelector('[data-fbt-row][hidden]') != null : false;
    for (const btn of this.querySelectorAll('[data-fbt-pick-another]')) {
      btn.toggleAttribute('disabled', !hasReserve);
    }
  }

  /* --------------------------------------------------------------------- */
  /* Variant / size synchronisation                                        */
  /* --------------------------------------------------------------------- */

  /** @returns {{name: string, value: string}[]} the main product's selected options */
  #readMainOptions() {
    const scope = this.#section instanceof Element ? this.#section : document;
    /** @type {{name: string, value: string}[]} */
    const opts = [];
    for (const select of scope.querySelectorAll('variant-picker select')) {
      const selected = /** @type {HTMLSelectElement} */ (select).selectedOptions[0];
      if (selected?.dataset?.optionName) opts.push({ name: selected.dataset.optionName, value: selected.value });
    }
    for (const input of scope.querySelectorAll('variant-picker fieldset input:checked')) {
      const el = /** @type {HTMLInputElement} */ (input);
      if (el.dataset?.optionName) opts.push({ name: el.dataset.optionName, value: el.value });
    }
    return opts;
  }

  /**
   * Finds the best-matching AVAILABLE variant option for a suggested row given the main
   * product's selected options: exact option-value matches score highest, then nearest
   * size (by ordinal distance) even across differently-named size options.
   * @param {HTMLSelectElement} select
   * @returns {HTMLOptionElement | null}
   */
  #bestVariant(select) {
    if (this.#mainOptions.length === 0) return null;
    const row = select.closest('[data-fbt-row]');
    const optionNames = (row instanceof HTMLElement ? row.dataset.optionNames || '' : '').split('~');

    let best = null;
    let bestScore = 0;

    for (const option of select.options) {
      if (option.dataset.available !== 'true') continue;
      const values = [option.dataset.opt1, option.dataset.opt2, option.dataset.opt3];
      let score = 0;

      for (const target of this.#mainOptions) {
        const idx = optionNames.findIndex((n) => n.toLowerCase() === target.name.toLowerCase());
        const named = idx >= 0 ? values[idx] : null;

        if (named != null && named !== '') {
          if (named.toLowerCase() === target.value.toLowerCase()) {
            score += 100;
          } else {
            const d = sizeDistance(named, target.value);
            if (d != null) score += Math.max(0, 10 - d);
          }
          continue;
        }

        // Cross-named size fallback: match a size value against any option value.
        if (sizeRank(target.value) >= 0) {
          let closest = null;
          for (const val of values) {
            const d = sizeDistance(val, target.value);
            if (d != null && (closest == null || d < closest)) closest = d;
          }
          if (closest != null) score += Math.max(0, 8 - closest);
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = option;
      }
    }

    return bestScore > 0 ? best : null;
  }

  /** @param {Element} row */
  #syncRow(row) {
    const select = /** @type {HTMLSelectElement | null} */ (row.querySelector('[data-fbt-variant-select]'));
    if (!select) return;
    const best = this.#bestVariant(select);
    if (best && best.value !== select.value) select.value = best.value;
    this.#applyVariant(select);
  }

  /* --------------------------------------------------------------------- */
  /* Row state + discount display                                          */
  /* --------------------------------------------------------------------- */

  /** @param {HTMLSelectElement} select */
  #applyVariant(select) {
    const row = select.closest('[data-fbt-row]');
    if (!(row instanceof HTMLElement)) return;
    const option = select.options[select.selectedIndex];
    if (!option) return;

    row.dataset.variantId = option.value;
    row.dataset.price = option.dataset.price || '0';
    row.dataset.compare = option.dataset.compare || '0';
    row.dataset.available = option.dataset.available || 'true';
    row.dataset.money = option.dataset.money || '';
    this.#applyDiscountDisplay(row);
  }

  /**
   * Shows the bundle discount on suggested rows (original → discounted → "Save X%").
   * The locked current-product row keeps its normal price.
   * @param {HTMLElement} row
   */
  #applyDiscountDisplay(row) {
    const priceEl = row.querySelector('[data-fbt-price]');
    const compareEl = /** @type {HTMLElement | null} */ (row.querySelector('[data-fbt-compare]'));
    const saveEl = /** @type {HTMLElement | null} */ (row.querySelector('[data-fbt-save]'));

    const original = Number(row.dataset.price) || 0;
    const compareCents = Number(row.dataset.compare) || 0;
    const pct = this.#discountPct;
    const isLocked = row.dataset.fbtLocked !== undefined;

    if (!isLocked && pct > 0 && original > 0) {
      const discounted = original - Math.round((original * pct) / 100);
      if (priceEl) priceEl.textContent = this.#money(discounted);
      if (compareEl) {
        compareEl.textContent = this.#money(original);
        compareEl.hidden = false;
      }
      if (saveEl) {
        saveEl.textContent = `Save ${pct}%`;
        saveEl.hidden = false;
      }
    } else {
      if (priceEl) priceEl.textContent = row.dataset.money || this.#money(original);
      if (saveEl) saveEl.hidden = true;
      if (compareEl) {
        if (compareCents > original) {
          compareEl.textContent = this.#money(compareCents);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }
  }

  /** @param {Element | null} row @param {number} delta */
  #stepQuantity(row, delta) {
    if (!(row instanceof HTMLElement)) return;
    const current = Math.max(1, Number(row.dataset.qty) || 1);
    const next = Math.min(99, Math.max(1, current + delta));
    row.dataset.qty = String(next);
    const valueEl = row.querySelector('[data-fbt-qty-value]');
    if (valueEl) valueEl.textContent = String(next);
  }

  /** @param {Element | null} row */
  #reflectSelection(row) {
    if (!(row instanceof HTMLElement)) return;
    const checkbox = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-fbt-select]'));
    row.classList.toggle('fbt-row--deselected', Boolean(checkbox) && !checkbox.checked);
  }

  /** @param {HTMLElement} row @returns {boolean} */
  #isSelected(row) {
    if (row.dataset.fbtLocked !== undefined) return true;
    if (row.dataset.available === 'false') return false;
    const checkbox = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-fbt-select]'));
    return checkbox ? checkbox.checked : true;
  }

  /* --------------------------------------------------------------------- */
  /* Totals (discount applies to suggested products only)                  */
  /* --------------------------------------------------------------------- */

  #updateTotals() {
    let subtotal = 0;
    let discount = 0;
    let selectedCount = 0;

    for (const row of this.#activeRows()) {
      if (!(row instanceof HTMLElement)) continue;
      this.#reflectSelection(row);
      if (!this.#isSelected(row)) continue;

      const qty = Math.max(1, Number(row.dataset.qty) || 1);
      const price = Number(row.dataset.price) || 0;
      subtotal += price * qty;
      if (row.dataset.fbtLocked === undefined) discount += Math.round((price * this.#discountPct) / 100) * qty;
      selectedCount += 1;
    }

    const final = subtotal - discount;

    const subtotalEl = this.querySelector('[data-fbt-subtotal]');
    if (subtotalEl) subtotalEl.textContent = this.#money(subtotal);

    const discountLine = this.querySelector('[data-fbt-discount-line]');
    const discountEl = this.querySelector('[data-fbt-discount]');
    if (discountLine) {
      if (discount > 0 && discountEl) {
        discountEl.textContent = `-${this.#money(discount)}`;
        discountLine.removeAttribute('hidden');
      } else {
        discountLine.setAttribute('hidden', '');
      }
    }

    const finalEl = this.querySelector('[data-fbt-final]');
    if (finalEl) finalEl.textContent = this.#money(final);

    const addAll = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-fbt-add-all]'));
    if (addAll) addAll.toggleAttribute('disabled', selectedCount === 0);
  }

  /** @param {number} cents */
  #money(cents) {
    try {
      return formatMoney(cents, this.#moneyFormat, this.#currency);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /* --------------------------------------------------------------------- */
  /* Events                                                                */
  /* --------------------------------------------------------------------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLElement} */ (event.target).closest('[data-fbt-variant-select]');
    if (select) this.#applyVariant(/** @type {HTMLSelectElement} */ (select));
    this.#updateTotals();
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const plus = target.closest('[data-fbt-qty-plus]');
    const minus = target.closest('[data-fbt-qty-minus]');
    if (plus || minus) {
      this.#stepQuantity(target.closest('[data-fbt-row]'), plus ? 1 : -1);
      this.#updateTotals();
      return;
    }

    const pick = target.closest('[data-fbt-pick-another]');
    if (pick) {
      event.preventDefault();
      this.#pickAnother(/** @type {HTMLElement} */ (pick));
      return;
    }

    if (target.closest('[data-fbt-add-all]')) {
      event.preventDefault();
      this.#addAll(/** @type {HTMLButtonElement} */ (target.closest('[data-fbt-add-all]')));
    }
  };

  /** @param {any} event */
  #onProductSelect = (event) => {
    this.#mainOptions =
      Array.isArray(event?.selectedOptions) && event.selectedOptions.length
        ? event.selectedOptions
        : this.#readMainOptions();
    for (const row of this.#activeRows()) this.#syncRow(row);
    this.#updateTotals();
  };

  /* --------------------------------------------------------------------- */
  /* Add all to cart (AJAX)                                                */
  /* --------------------------------------------------------------------- */

  /** @param {HTMLButtonElement} button */
  async #addAll(button) {
    /** @type {Array<{ id: number, quantity: number }>} */
    const items = [];

    for (const row of this.#activeRows()) {
      if (!(row instanceof HTMLElement) || !this.#isSelected(row)) continue;
      const id = Number(row.dataset.variantId);
      const quantity = Math.max(1, Number(row.dataset.qty) || 1);
      if (id) items.push({ id, quantity });
    }

    if (items.length === 0) return;

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      const cart = await fetch(`${cartUrl}.js`).then((res) => res.json());

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'frequently-bought-together', sourceId: this.id },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'frequently-bought-together',
          sourceId: this.id,
          itemCount: totalQuantity,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}
